import {
  InstantProducts as HeadlessInstantProducts,
  SearchBox as HeadlessSearchBox,
  buildInstantProducts,
  buildStandaloneSearchBox,
} from '@coveo/headless/commerce';

import {
  Button,
  CtaLink,
  IconButton,
  LayoutContainer,
  NavGsLogo,
  PageSection,
  PageSectionContent,
} from '@greystar/designsystem';

import {
  getDictionaryEntry,
  getEngine,
  SEARCHBOX_SUBTITLE_FALLBACK,
  getSiteBaseUrl,
  setFocus,
  useAnalytics,
  useFormattedAddress,
} from '@greystar/lib';
import { EventProperty } from '@greystar/lib/src/types/property-types';
import { Placeholder, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { useI18n } from 'next-localization';
import { useEffect, useState, JSX } from 'react';
import { ComponentProps } from '../../../component-props';
import { SearchBoxCsr } from '../../../components/search/search-box/search-box-csr';
import styles from './header-csr.module.scss';
import useCloudSDK from '@greystar/ui/src/hooks/useCloudSDK';
import { personalize } from '@sitecore-cloudsdk/personalize/browser';
import { CONSENT_COOKIE_NAME } from '@greystar/ui/src/hooks/useOneTrust';
import useCookieListener from '@greystar/ui/src/hooks/useCookieListener';

// Define interface for NavLink based on provided data
interface NavLink {
  id: string;
  url: string;
  name: string;
  displayName: string;
  fields: {
    colour: { value: string };
    Link: {
      value: {
        href: string;
        text: string;
        linktype: string;
        url: string;
        anchor: string;
        target: string;
      };
    };
  };
}

// Extend ComponentProps to include onModalClick
interface HeaderCsrProps extends ComponentProps {
  onModalClick?: () => void;
}

export const handleModalClick = () => {};

type PersonalizeResponse = {
  alternativeSearchBar?: boolean;
};

const HeaderCsr = (props: HeaderCsrProps): JSX.Element => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { sitecoreContext } = useSitecoreContext();
  const controllerSearchBox: HeadlessSearchBox = buildStandaloneSearchBox(getEngine(), {
    options: {
      redirectionUrl: '/s',
      id: 'searchBoxHeader',
    },
  });
  const analytics = useAnalytics();
  const property: EventProperty = sitecoreContext?.property ?? {};
  const location = property.location;
  const property_location = useFormattedAddress(property.location);
  const [showPeronalizedSearchBar, setShowPeronalizedSearchBar] = useState(false);
  const [personalizeLoading, setPersonalizeLoading] = useState(false);
  const { isPersonalizeEnabled } = useCloudSDK();
  const isPropertyDetailPage = sitecoreContext?.route?.templateName?.includes('Property');
  const { cookie: oneTrustCookie } = useCookieListener(CONSENT_COOKIE_NAME);
  const [forceShowElements, setForceShowElements] = useState(false);

  useEffect(() => {
    if (!isPropertyDetailPage) {
      setForceShowElements(true);
      return;
    }

    const timer = setTimeout(() => {
      if (!oneTrustCookie) {
        setForceShowElements(true);
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, [oneTrustCookie, isPropertyDetailPage]);

  const controllerInstantProducts: HeadlessInstantProducts = buildInstantProducts(getEngine(), {
    options: {
      searchBoxId: 'searchBoxHeader',
    },
  });

  const handleLogoClick = () => {
    const eventData = {
      event: 'header_interaction',
      action_type: 'greystar logo click',
      state: location?.state ?? '',
      property_location: property_location,
      property_id: property.id ?? '',
      property_name: property.name ?? '',
      content_group: 'header',
      module_name: 'navigation bar',
      inpage_location: 'top',
      click_text: 'Greystar Logo',
      click_type: 'internal',
      destination_url: '/',
    };

    analytics.trackEvent('header_interaction', eventData);
  };

  const handleLinkClick = (label: string, link: string) => {
    const location = property?.location ?? {};
    const eventData = {
      event: 'header_interaction',
      action_type: 'link click - header',
      state: location?.state ?? '',
      property_location: property_location,
      property_id: property.id ?? '',
      property_name: property.name ?? '',
      content_group: 'header',
      module_name: 'navigation bar',
      inpage_location: 'top',
      click_text: label ?? '',
      click_type: 'internal',
      destination_url: link ?? '',
    };

    analytics.trackEvent('header_interaction', eventData);
  };

  const handleModalClick = () => {
    setIsModalOpen((prev) => !prev);
  };

  useEffect(() => {
    if (isModalOpen && typeof setFocus === 'function') {
      setFocus('modal');
    }
  }, [isModalOpen]);

  const { t } = useI18n();
  const isHomePage = sitecoreContext?.route?.name === 'Home';
  const isCalculatorPage = sitecoreContext?.route?.name === 'calculator';

  // Safely access pageHeaderColor
  const pageHeaderColor = sitecoreContext?.route?.fields?.pageHeaderColor;
  const headerColor =
    typeof pageHeaderColor === 'object' &&
    'value' in pageHeaderColor &&
    typeof pageHeaderColor.value === 'string'
      ? pageHeaderColor.value
      : 'white';

  const pageNavLinks = sitecoreContext?.route?.fields?.pageNavLinks;
  const isPageNavLinksArray = Array.isArray(pageNavLinks);

  const showSearchDropdown = !isHomePage && (showPeronalizedSearchBar || !isPropertyDetailPage);
  const showSearchIconButton = !isHomePage && !isCalculatorPage && !isPropertyDetailPage;
  const showSearchButton = !showPeronalizedSearchBar;
  const showCtaLinks = isHomePage && isPageNavLinksArray;
  const canShowSearch = (oneTrustCookie || forceShowElements) && !personalizeLoading;

  useEffect(() => {
    if (!isPropertyDetailPage) {
      setPersonalizeLoading(false);
      setShowPeronalizedSearchBar(false);
      return;
    }
    if (!isPersonalizeEnabled) {
      setPersonalizeLoading(false);
      setShowPeronalizedSearchBar(false);
      return;
    }
    setPersonalizeLoading(true);

    const getPersonalizeResponse = async () => {
      try {
        const personalizeResponse = await personalize({
          channel: 'WEB',
          currency: 'USD',
          friendlyId: 'search_bar_prominence',
        });
        const res = personalizeResponse as PersonalizeResponse;
        const searchBarRes = res?.alternativeSearchBar ?? false;
        setShowPeronalizedSearchBar(searchBarRes);

        analytics.trackExperimentEvent({
          experimentId: 'search_bar_prominence',
          variantName: searchBarRes ? 'alternative_search_bar' : 'default_search_bar',
        });
      } catch (error) {
        console.debug('CloudSDK personalize not initialized, using default search bar:', error);
      } finally {
        setPersonalizeLoading(false);
      }
    };

    getPersonalizeResponse();
  }, [isPersonalizeEnabled, analytics, isPropertyDetailPage]);

  return (
    <PageSection isTopFlushed isBottomFlushed topColour={headerColor}>
      <PageSectionContent hasMaxWidth>
        <LayoutContainer
          justifyContent="spaceBetween"
          alignItems="center"
          flexDirection="row"
          gap="md"
          paddingTop="xs"
          paddingBottom="xs"
        >
          <NavGsLogo
            path={getSiteBaseUrl('Editorial')}
            label={getDictionaryEntry(t('label.greystar'), 'greystar')}
            onClick={handleLogoClick}
          />
          {!isHomePage && (
            <div
              className={`${styles['search-dropdown-container']} ${
                showSearchDropdown && canShowSearch
                  ? 'opacity-100 pointer-events-auto'
                  : 'opacity-0 pointer-events-none'
              } ${isPropertyDetailPage ? 'md:transition-opacity md:duration-1000' : ''}`}
            >
              <SearchBoxCsr
                controllerSearchBox={controllerSearchBox}
                controllerInstantProducts={controllerInstantProducts}
                searchPromptText={getDictionaryEntry(t('searchbox_prompttext'), 'Search for homes')}
                Input={{
                  size: 'small',
                  inputProps: {
                    placeholder: getDictionaryEntry(
                      t('searchbox_subtitle'),
                      SEARCHBOX_SUBTITLE_FALLBACK
                    ),
                  },
                }}
                ModalTrigger={{
                  label: getDictionaryEntry(t('searchbox_title'), 'Search for your home'),
                  onClick: props.onModalClick || handleModalClick,
                }}
                Results={{
                  labelProps: {
                    label: getDictionaryEntry(t('searchbox_resultstext'), 'Search Results'),
                  },
                }}
              />
            </div>
          )}
          {isModalOpen && (
            <SearchBoxCsr
              variant="modal"
              controllerSearchBox={controllerSearchBox}
              controllerInstantProducts={controllerInstantProducts}
              headerTitle={getDictionaryEntry(t('searchbox_title'), 'Search for your home')}
              headerSubtitle={getDictionaryEntry(
                t('searchbox_subtitle'),
                SEARCHBOX_SUBTITLE_FALLBACK
              )}
              onClose={handleModalClick}
              searchPromptText={getDictionaryEntry(t('searchbox_prompttext'), 'Search for homes')}
              searchResultsTitle=""
            />
          )}
          <LayoutContainer gap="xs">
            {!isHomePage && (
              <div
                className={`${
                  isPropertyDetailPage
                    ? 'opacity-100 pointer-events-auto md:opacity-0 md:pointer-events-none'
                    : 'opacity-0 pointer-events-none'
                } ${isPropertyDetailPage ? 'md:transition-opacity md:duration-1000' : ''} ${
                  isPropertyDetailPage && showSearchButton && canShowSearch
                    ? 'md:opacity-100 md:pointer-events-auto'
                    : ''
                } ${
                  isPropertyDetailPage && showSearchDropdown
                    ? 'max-[768px]:!hidden max-[425px]:!block'
                    : ''
                } ${!isPropertyDetailPage ? 'min-[425px]:hidden' : ''}`}
              >
                <Button
                  onClick={handleModalClick}
                  label={
                    /** @ts-expect-error unable to serialize the property.location.city as it's coming from out of the box hook from coveo - useSitecoreContext() */
                    (sitecoreContext?.property?.location?.city as string) ?? 'Search for homes'
                  }
                  variant="secondary"
                  icon={{
                    position: 'left',
                    name: 'Search',
                  }}
                />
              </div>
            )}

            {showCtaLinks && (
              <LayoutContainer paddingRight="md" gap="md">
                {(pageNavLinks as NavLink[]).map((link) => {
                  return (
                    <CtaLink
                      key={link.id}
                      href={link.fields.Link.value.href}
                      variant="large-black"
                      label={link.displayName}
                      onClick={() => handleLinkClick(link.displayName, link.fields.Link.value.href)}
                    />
                  );
                })}
              </LayoutContainer>
            )}
            {showSearchIconButton && (
              <div className={styles['search-button-container']}>
                <IconButton
                  variant="secondary"
                  iconName="Search"
                  size="medium"
                  onClick={props.onModalClick || handleModalClick}
                />
              </div>
            )}
            <Placeholder
              name={`header-nav-menu-${props.params.DynamicPlaceholderId}`}
              rendering={props.rendering}
            />
          </LayoutContainer>
        </LayoutContainer>
      </PageSectionContent>
    </PageSection>
  );
};

export default HeaderCsr;
